import { useCallback, useEffect, useState } from 'react';
import { BarChart3, RefreshCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { analyticsService, TenantStateCountsResponse, StateCount, TenantPlatformStateCounts } from '../services/analyticsService';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import styles from '../styles/App.module.css';

const STATE_ORDER = ['PUBLISHED', 'SCHEDULED', 'UNPUBLISHED'];

const stateLabels: Record<string, string> = {
  PUBLISHED: 'Published',
  SCHEDULED: 'Scheduled',
  UNPUBLISHED: 'Unpublished',
};

const tenantLabels: Record<string, string> = {
  REELS: 'DashReels',
  SHORTFREE: 'ShortFree',
};

const platformLabels: Record<string, string> = {
  ANDROID: 'Android',
  IOS: 'iOS',
  WEB: 'Web',
  TIKTOK_MINIS: 'TikTok Minis',
};

const formatCount = (count: number) => new Intl.NumberFormat('en-US').format(count);

const getOrderedCounts = (counts: StateCount[]) => {
  const countsByState = new Map(counts.map(item => [item.state, item.count]));
  const knownCounts = STATE_ORDER.map(state => ({
    state,
    count: countsByState.get(state) || 0,
  }));
  const extraCounts = counts.filter(item => !STATE_ORDER.includes(item.state));

  return [...knownCounts, ...extraCounts];
};

function PlatformBreakdownTable({ rows }: { rows: TenantPlatformStateCounts[] }) {
  return (
    <div className="rounded-md border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50 text-left">
            <th className="px-3 py-2 font-medium text-muted-foreground">Platform</th>
            {STATE_ORDER.map(state => (
              <th key={state} className="px-3 py-2 font-medium text-muted-foreground text-right">
                {stateLabels[state]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const countsByState = new Map(row.counts.map(item => [item.state, item.count]));
            return (
              <tr key={row.platform} className="border-b last:border-b-0">
                <td className="px-3 py-2">{platformLabels[row.platform] || row.platform}</td>
                {STATE_ORDER.map(state => (
                  <td key={state} className="px-3 py-2 text-right tabular-nums">
                    {formatCount(countsByState.get(state) || 0)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CountGrid({ counts, singleRow = false }: { counts: StateCount[]; singleRow?: boolean }) {
  const orderedCounts = getOrderedCounts(counts);
  const totalCount = orderedCounts.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="space-y-4">
      <div className={cn(
        'grid grid-cols-1 sm:grid-cols-2 gap-4',
        singleRow && 'xl:grid-cols-4'
      )}>
        <Card className="border bg-card">
          <CardContent className="p-5">
            <div className="text-sm text-muted-foreground">Total shows</div>
            <div className="mt-2 text-3xl font-semibold">{formatCount(totalCount)}</div>
          </CardContent>
        </Card>

        {orderedCounts.map(item => (
          <Card key={item.state} className="border bg-card">
            <CardContent className="p-5">
              <div className="text-sm text-muted-foreground">
                {stateLabels[item.state] || item.state}
              </div>
              <div className="mt-2 text-3xl font-semibold">{formatCount(item.count)}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { isAuth, loading: authLoading } = useAuth();
  const [data, setData] = useState<TenantStateCountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    if (!isAuth) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getTenantStateCounts();
      setData(response);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [isAuth]);

  useEffect(() => {
    if (!authLoading) {
      loadDashboard();
    }
  }, [authLoading, loadDashboard]);

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.contentWrapper}>
        <div className={styles.navigation}>
          <div className={styles.navContent}>
            <h1 className={styles.navTitle}>Dashboard</h1>
            <Button
              variant="outline"
              size="sm"
              onClick={loadDashboard}
              disabled={loading || !isAuth}
              className={styles.navButton}
            >
              <RefreshCcw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="px-4 py-6 space-y-6">
          {loading && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-28 rounded-lg" />
                ))}
              </div>
              <Skeleton className="h-64 rounded-lg" />
            </>
          )}

          {!loading && error && (
            <div className="rounded-lg border bg-card p-8 text-center">
              <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-lg font-semibold mb-2">Failed to load dashboard</h2>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={loadDashboard}>Try again</Button>
            </div>
          )}

          {!loading && !error && data && (
            <>
              <section className="space-y-3">
                <div>
                  <h2 className="text-base font-semibold">All tenants</h2>
                  <p className="text-sm text-muted-foreground">
                    Total show counts by publishing status.
                  </p>
                </div>
                <CountGrid counts={data.total || []} singleRow />
              </section>

              <section className="space-y-3">
                <div>
                  <h2 className="text-base font-semibold">Tenant breakdown</h2>
                  <p className="text-sm text-muted-foreground">
                    Publishing status counts for each tenant.
                  </p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {data.tenantBreakdown.map(item => {
                    const platformRows = (data.tenantPlatformBreakdown || []).filter(
                      row => row.tenant === item.tenant
                    );
                    return (
                      <Card key={item.tenant} className="border bg-card">
                        <CardContent className="p-5 space-y-4">
                          <h3 className="text-sm font-semibold">
                            {tenantLabels[item.tenant] || item.tenant}
                          </h3>
                          <CountGrid counts={item.counts} />
                          {platformRows.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs text-muted-foreground">
                                By platform (a show can appear in multiple state columns across platforms)
                              </p>
                              <PlatformBreakdownTable rows={platformRows} />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
